import { AlertTriangle, ShieldCheck } from "lucide-react";

import { allergySeverityLabel, formatDateTime } from "@/shared/format";
import { allergyOverrideReasonLabel } from "@/shared/types/patient";
import type {
  PrescriptionAllergyConflict,
  PrescriptionAllergyOverride,
} from "@/shared/types/prescription";

const MATCHED_ON_LABEL: Record<PrescriptionAllergyConflict["matchedOn"], string> = {
  CODE: "trùng mã thuốc",
  ACTIVE_INGREDIENT: "trùng hoạt chất",
  NAME: "trùng tên thuốc",
};

/**
 * Drug allergies recorded AFTER a medicine was already on this prescription (#18861).
 *
 * <p>The #18859 dialog fires when the doctor picks a medicine. It cannot fire for the reverse
 * order — a nurse records a Penicillin allergy at 10:10 for a draft written at 10:00 — because
 * nothing in the prescribing screen was listening. This banner is that case: the conflicts are
 * recomputed on every read of an open prescription, so the next person to open it sees them.
 *
 * <p>Deliberately NOT a blocking dialog. Opening a record is not the moment to demand a decision,
 * and the decision is demanded anyway where it counts — the server refuses to finalize the
 * prescription until each conflict carries an audited override. This is the early notice; the
 * finalize guard is the safety net.
 *
 * <p>Same vocabulary as #18859 — severity, reaction, and why it matched — because a doctor who
 * learned to read one allergy warning should not have to learn a second.
 */
export function PrescriptionAllergyConflicts({
  conflicts,
}: {
  conflicts: PrescriptionAllergyConflict[];
}) {
  const outstanding = conflicts.filter((c) => !c.overridden);
  if (outstanding.length === 0) {
    // Nothing, not "no conflicts" — an all-clear here would be a claim about the patient that an
    // empty allergy list cannot support.
    return null;
  }
  const severe = outstanding.some((c) => c.severe);

  return (
    <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
        <AlertTriangle className="size-4" />
        {severe ? "Cảnh báo dị ứng NẶNG" : "Cảnh báo dị ứng"}
      </div>
      <p className="mb-2 mt-1 text-sm text-muted-foreground">
        Đơn thuốc này có thuốc bệnh nhân đang được ghi nhận dị ứng. Cần xác nhận và nêu lý do trước
        khi hoàn tất đơn.
      </p>
      <ul className="space-y-2 text-sm">
        {outstanding.map((c) => (
          <li
            key={`${c.medicineId}-${c.allergyId}`}
            className="rounded border border-destructive/30 px-3 py-2"
          >
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-semibold">{c.medicineName}</span>
              <span className="text-muted-foreground">— dị ứng</span>
              <span className="font-medium">{c.substance}</span>
              <span
                className={
                  c.severe
                    ? "text-xs font-semibold text-destructive"
                    : "text-xs text-muted-foreground"
                }
              >
                {allergySeverityLabel[c.severity]}
              </span>
              <span className="text-xs text-muted-foreground">
                ({MATCHED_ON_LABEL[c.matchedOn]})
              </span>
            </div>
            {c.reaction && (
              <div className="mt-0.5 text-xs text-muted-foreground">Phản ứng: {c.reaction}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Lợi ích điều trị vượt trội nguy cơ", or for OTHER the doctor's own words. */
export function overrideReasonText(o: Pick<PrescriptionAllergyOverride, "reasonCode" | "reasonText">) {
  const label = allergyOverrideReasonLabel[o.reasonCode] ?? o.reasonCode;
  if (o.reasonCode === "OTHER") return o.reasonText?.trim() || label;
  return o.reasonText?.trim() ? `${label} (${o.reasonText.trim()})` : label;
}

/**
 * Drug-allergy warnings that WERE answered — who, why, when (#19184).
 *
 * <p>PO rule (#18859 note 3): an override must record who overrode, for what reason and when. The
 * row was recorded but nothing showed it, so nobody could check the reason had been kept. This is
 * that place, on the prescription itself, and it stays after the prescription is signed — that is
 * when a reviewer comes looking.
 *
 * <p>Not red: a decision a doctor took and justified is not an alarm. But it is not hidden either.
 */
export function PrescriptionAllergyOverrides({
  overrides,
}: {
  overrides: PrescriptionAllergyOverride[];
}) {
  if (overrides.length === 0) return null;
  return (
    <div
      className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3"
      data-testid="allergy-overrides"
    >
      <div className="flex items-center gap-1.5 text-sm font-semibold">
        <ShieldCheck className="size-4 text-amber-600" />
        Cảnh báo dị ứng đã được xác nhận
      </div>
      <ul className="mt-2 space-y-1.5 text-sm">
        {overrides.map((o) => (
          <li key={o.id} className="leading-snug">
            Đã xác nhận dị ứng: <span className="font-medium">{o.medicineName}</span> ·{" "}
            <span className="font-medium">{o.substance}</span> ·{" "}
            {allergySeverityLabel[o.severity] ?? o.severity}
            {" — "}Lý do: <span className="font-medium">{overrideReasonText(o)}</span> ·{" "}
            {o.overriddenByName ?? "Không rõ người xác nhận"} · {formatDateTime(o.overriddenAt)}
          </li>
        ))}
      </ul>
    </div>
  );
}
